class HasthrangStickyAtc {
  constructor(element) {
    this.element = element;
    this.formId = element.dataset.productFormId;
    this.productInfo = element.closest('product-info');
    this.variantSelect = element.querySelector('[data-hasthrang-sticky-variant]');
    this.quantityInput = element.querySelector('[data-hasthrang-sticky-quantity]');
    this.minusButton = element.querySelector('[data-hasthrang-sticky-minus]');
    this.plusButton = element.querySelector('[data-hasthrang-sticky-plus]');
    this.submitButton = element.querySelector('[data-hasthrang-sticky-submit]');
    this.price = element.querySelector('[data-hasthrang-sticky-price]');
    this.compare = element.querySelector('[data-hasthrang-sticky-compare]');
    this.error = element.querySelector('[data-hasthrang-sticky-error]');
    this.confirmedVariantId = '';
    this.pendingSubmitVariantId = '';
    this.syncRequestId = 0;
    this.isSubmitting = false;
    this.productDataUrl = '';
    this.productDataPromise = null;
    this.mainSyncTimer = null;

    this.refreshForm();
    if (!this.productInfo || !this.form || !this.variantSelect || !this.quantityInput || !this.submitButton) return;

    this.element.dataset.hasthrangStickyInitialized = 'true';
    this.syncFromCurrentVariant();
    this.bindEvents();
    this.bindVisibility();
    this.observeRerenders();
    this.syncVariantDisplay();
    this.queueMainSelectionSync();
  }

  refreshForm() {
    this.form = document.getElementById(this.formId);
  }

  bindEvents() {
    this.variantSelect.addEventListener('change', () => this.handleStickyVariantChange());
    this.minusButton?.addEventListener('click', () => this.adjustQuantity(-1));
    this.plusButton?.addEventListener('click', () => this.adjustQuantity(1));
    this.submitButton.addEventListener('click', () => this.submit());

    this.productInfo.addEventListener(
      'submit',
      (event) => {
        if (event.target?.getAttribute?.('id') === this.formId) this.enforceSubmittedVariant();
      },
      true
    );

    this.productInfo.addEventListener('change', (event) => {
      if (event.target === this.variantSelect) return;
      if (event.target.matches?.('input[name="id"]') || event.target.closest?.('variant-selects')) {
        this.queueMainSelectionSync();
      }
    });

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
      this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
        if (!this.element.isConnected) {
          this.variantChangeUnsubscriber?.();
          this.variantChangeUnsubscriber = null;
          return;
        }

        if (data?.sectionId && `product-form-${data.sectionId}` !== this.formId) return;
        this.syncFromMainVariant(data?.variant, true);
      });
    }

    document.addEventListener('hasthrang:cart-error', (event) => {
      if (!this.element.isConnected) return;
      this.isSubmitting = false;
      this.setLoading(false);
      this.showError(event.detail?.message || window.cartStrings?.error || 'Unable to add item to cart.');
    });

    document.addEventListener('hasthrang:cart-added', () => {
      if (!this.element.isConnected) return;
      this.isSubmitting = false;
      this.setLoading(false);
      this.showError('');
    });
  }

  bindVisibility() {
    const mainButton = document.querySelector(`#ProductSubmitButton-${this.formId.replace('product-form-', '')}`);

    if (!mainButton || !('IntersectionObserver' in window)) {
      this.element.hidden = window.scrollY < 500;
      window.addEventListener(
        'scroll',
        () => {
          if (this.element.isConnected) this.element.hidden = window.scrollY < 500;
        },
        { passive: true }
      );
      return;
    }

    this.visibilityObserver = new IntersectionObserver(
      ([entry]) => {
        this.element.hidden = entry.isIntersecting;
      },
      { threshold: 0 }
    );
    this.visibilityObserver.observe(mainButton);
  }

  observeRerenders() {
    this.rerenderObserver = new MutationObserver((mutations) => {
      const variantUiChanged = mutations.some((mutation) =>
        [...mutation.addedNodes, ...mutation.removedNodes].some((node) => {
          if (node.nodeType !== Node.ELEMENT_NODE) return false;
          return (
            node.matches?.('variant-selects, product-form, form[data-type="add-to-cart-form"]') ||
            node.querySelector?.('variant-selects, product-form, form[data-type="add-to-cart-form"]')
          );
        })
      );

      if (variantUiChanged) {
        this.refreshForm();
        this.queueMainSelectionSync();
      }
    });

    this.rerenderObserver.observe(this.productInfo, { childList: true, subtree: true });
  }

  syncFromCurrentVariant() {
    const variantInput = this.form.querySelector('[name="id"]');
    const urlVariantId = new URLSearchParams(window.location.search).get('variant');
    const variantId = variantInput?.value || urlVariantId;

    if (variantId && this.hasStickyVariant(variantId)) {
      this.variantSelect.value = String(variantId);
      this.confirmedVariantId = String(variantId);
    }
  }

  syncFromMainVariant(variant, confirmed = false) {
    const variantId = variant?.id ? String(variant.id) : '';
    if (!variantId || !this.hasStickyVariant(variantId)) return;

    if (confirmed) this.confirmedVariantId = variantId;
    this.variantSelect.value = variantId;
    this.syncVariantDisplay();
    this.showError('');
  }

  async syncFromMainSelection() {
    if (!this.element.isConnected) return;

    this.refreshForm();
    const variantInputId = this.form?.querySelector('[name="id"]')?.value;
    if (variantInputId && this.hasStickyVariant(variantInputId)) {
      this.syncFromMainVariant({ id: variantInputId }, false);
    }

    try {
      const productData = await this.getProductData();
      const selectedOptions = this.getSelectedMainOptionValues();
      const variant = productData.variants?.find(
        (item) =>
          item.options?.length === selectedOptions.length &&
          item.options.every((value, index) => String(value) === selectedOptions[index])
      );

      if (variant) this.syncFromMainVariant(variant, false);
    } catch {
      return;
    }
  }

  queueMainSelectionSync() {
    window.clearTimeout(this.mainSyncTimer);
    this.mainSyncTimer = window.setTimeout(() => this.syncFromMainSelection(), 0);
  }

  hasStickyVariant(variantId) {
    return Array.from(this.variantSelect.options).some((option) => option.value === String(variantId));
  }

  handleStickyVariantChange() {
    this.syncRequestId += 1;
    this.confirmedVariantId = '';
    this.syncVariantDisplay();
    this.showError('');
    this.syncMainVariant(this.variantSelect.value);
  }

  syncVariantDisplay() {
    const option = this.variantSelect.selectedOptions[0];
    if (!option) return;

    const isAvailable = option.dataset.available === 'true';
    this.submitButton.disabled = this.isSubmitting || !isAvailable;
    this.submitButton.textContent = isAvailable
      ? window.variantStrings?.addToCart || 'Add to cart'
      : window.variantStrings?.soldOut || 'Sold out';
    this.price.textContent = option.dataset.price || '';

    if (option.dataset.compare) {
      this.compare.textContent = option.dataset.compare;
      this.compare.hidden = false;
    } else {
      this.compare.hidden = true;
    }
  }

  getVariantSelectors() {
    return this.productInfo?.querySelector('variant-selects');
  }

  getSelectedMainOptionValues() {
    const variantSelectors = this.getVariantSelectors();
    if (!variantSelectors) return [];

    return Array.from(variantSelectors.querySelectorAll('fieldset, .product-form__input--dropdown'))
      .map((group) => group.querySelector('input:checked')?.value || group.querySelector('select')?.value || '')
      .filter(Boolean)
      .map(String);
  }

  getProductJsonUrl() {
    const productUrl = new URL(this.productInfo?.dataset.url || window.location.pathname, window.location.origin);
    productUrl.search = '';
    productUrl.hash = '';
    productUrl.pathname = `${productUrl.pathname.replace(/\/$/, '')}.js`;
    return productUrl.toString();
  }

  getProductData() {
    const productDataUrl = this.getProductJsonUrl();
    if (this.productDataPromise && this.productDataUrl === productDataUrl) return this.productDataPromise;

    this.productDataUrl = productDataUrl;
    this.productDataPromise = fetch(productDataUrl, { headers: { Accept: 'application/json' } })
      .then((response) => {
        if (!response.ok) throw new Error('Unable to load product variants.');
        return response.json();
      })
      .catch((error) => {
        this.productDataPromise = null;
        throw error;
      });

    return this.productDataPromise;
  }

  async getVariantOptionValues(variantId) {
    const productData = await this.getProductData();
    const variant = productData.variants?.find((item) => String(item.id) === String(variantId));
    return variant?.options?.map(String) || [];
  }

  mainSelectorMatches(optionValues) {
    const selectedOptions = this.getSelectedMainOptionValues();
    if (!this.getVariantSelectors()) {
      const variantInputId = this.form?.querySelector('[name="id"]')?.value;
      return (
        optionValues.length === 1 &&
        this.variantSelect.options.length === 1 &&
        String(variantInputId || '') === this.variantSelect.value
      );
    }

    return (
      optionValues.length > 0 &&
      selectedOptions.length === optionValues.length &&
      optionValues.every((value, index) => value === selectedOptions[index])
    );
  }

  setVariantInput(variantId) {
    this.refreshForm();
    const variantInput = this.form?.querySelector('[name="id"]');
    if (!variantInput) return false;

    variantInput.value = String(variantId);
    variantInput.disabled = false;
    return variantInput.value === String(variantId);
  }

  async syncMainVariant(variantId) {
    const requestedVariantId = String(variantId);
    const requestId = ++this.syncRequestId;
    let optionValues;

    try {
      optionValues = await this.getVariantOptionValues(requestedVariantId);
    } catch {
      return false;
    }

    if (!optionValues.length || requestId !== this.syncRequestId) return false;

    const variantSelectors = this.getVariantSelectors();
    if (!variantSelectors) {
      const productData = await this.getProductData();
      if (productData.variants?.length !== 1) return false;
      this.confirmedVariantId = requestedVariantId;
      return this.setVariantInput(requestedVariantId);
    }

    if (this.confirmedVariantId === requestedVariantId && this.mainSelectorMatches(optionValues)) {
      return this.setVariantInput(requestedVariantId);
    }

    const groups = Array.from(variantSelectors.querySelectorAll('fieldset, .product-form__input--dropdown'));
    if (groups.length !== optionValues.length) return false;

    let eventTarget = null;

    for (let index = 0; index < groups.length; index += 1) {
      const value = optionValues[index];
      const select = groups[index].querySelector('select');

      if (select) {
        const selectedOption = Array.from(select.options).find((option) => option.value === value);
        if (!selectedOption) return false;
        Array.from(select.options).forEach((option) => option.removeAttribute('selected'));
        select.value = value;
        selectedOption.setAttribute('selected', 'selected');
        eventTarget = select;
        continue;
      }

      const input = Array.from(groups[index].querySelectorAll('input')).find((item) => item.value === value);
      if (!input) return false;
      input.checked = true;
      eventTarget = input;
    }

    if (!eventTarget) return false;

    const synced = await this.waitForVariantChange(requestedVariantId, optionValues, () => {
      eventTarget.dispatchEvent(new Event('change', { bubbles: true }));
    });

    if (requestId !== this.syncRequestId || !synced || !this.mainSelectorMatches(optionValues)) return false;

    this.confirmedVariantId = requestedVariantId;
    return this.setVariantInput(requestedVariantId);
  }

  waitForVariantChange(variantId, optionValues, trigger) {
    if (
      typeof subscribe !== 'function' ||
      typeof PUB_SUB_EVENTS === 'undefined' ||
      !PUB_SUB_EVENTS.variantChange
    ) {
      trigger();
      return new Promise((resolve) => {
        window.setTimeout(() => resolve(this.mainSelectorMatches(optionValues)), 250);
      });
    }

    return new Promise((resolve) => {
      let timeoutId;
      const unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
        if (data?.sectionId && `product-form-${data.sectionId}` !== this.formId) return;
        if (String(data?.variant?.id || '') !== String(variantId)) return;

        window.clearTimeout(timeoutId);
        unsubscribe();
        this.confirmedVariantId = String(variantId);
        resolve(true);
      });

      timeoutId = window.setTimeout(() => {
        unsubscribe();
        resolve(false);
      }, 6000);

      trigger();
    });
  }

  adjustQuantity(direction) {
    const step = parseInt(this.quantityInput.step, 10) || 1;
    const min = parseInt(this.quantityInput.min, 10) || 1;
    const current = parseInt(this.quantityInput.value, 10) || min;
    this.quantityInput.value = Math.max(min, current + direction * step);
  }

  async submit() {
    if (this.isSubmitting) return;

    const option = this.variantSelect.selectedOptions[0];
    if (!option || option.dataset.available !== 'true') return;

    const variantId = this.variantSelect.value;
    this.isSubmitting = true;
    this.setLoading(true);

    const isSynced = await this.syncMainVariant(variantId);
    let optionValues = [];
    try {
      optionValues = await this.getVariantOptionValues(variantId);
    } catch {
      optionValues = [];
    }

    if (
      !isSynced ||
      this.confirmedVariantId !== String(variantId) ||
      !this.mainSelectorMatches(optionValues) ||
      !this.element.isConnected
    ) {
      this.isSubmitting = false;
      this.showError('Please select your size again.');
      this.setLoading(false);
      return;
    }

    this.refreshForm();
    const variantInput = this.form?.querySelector('[name="id"]');
    const quantityInput = this.productInfo.querySelector(`[name="quantity"][form="${this.formId}"]`);

    if (!variantInput) {
      this.isSubmitting = false;
      this.showError(window.cartStrings?.error || 'Unable to add item to cart.');
      this.setLoading(false);
      return;
    }

    this.pendingSubmitVariantId = String(variantId);
    this.enforceSubmittedVariant();

    if (variantInput.value !== this.pendingSubmitVariantId) {
      this.pendingSubmitVariantId = '';
      this.isSubmitting = false;
      this.showError('Please select your size again.');
      this.setLoading(false);
      return;
    }

    if (quantityInput) {
      quantityInput.value = Math.max(
        parseInt(this.quantityInput.min, 10) || 1,
        parseInt(this.quantityInput.value, 10) || 1
      );
    }

    this.showError('');
    const mainSubmitButton = this.form.querySelector('[type="submit"][name="add"]');

    if (typeof this.form.requestSubmit === 'function' && mainSubmitButton) {
      this.form.requestSubmit(mainSubmitButton);
    } else {
      this.form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    }

    this.pendingSubmitVariantId = '';
  }

  enforceSubmittedVariant() {
    if (!this.pendingSubmitVariantId) return;
    this.setVariantInput(this.pendingSubmitVariantId);
  }

  setLoading(isLoading) {
    const isAvailable = this.variantSelect.selectedOptions[0]?.dataset.available === 'true';
    this.submitButton.disabled = isLoading || !isAvailable;
    this.submitButton.textContent = isLoading
      ? 'Adding...'
      : isAvailable
        ? window.variantStrings?.addToCart || 'Add to cart'
        : window.variantStrings?.soldOut || 'Sold out';
  }

  showError(message) {
    if (this.error) this.error.textContent = message || '';
  }
}

function initializeHasthrangStickyAtc(root = document) {
  const elements = [];
  if (root.matches?.('[data-hasthrang-sticky-atc]')) elements.push(root);
  elements.push(...(root.querySelectorAll?.('[data-hasthrang-sticky-atc]') || []));

  elements.forEach((element) => {
    if (element.dataset.hasthrangStickyInitialized === 'true') return;
    new HasthrangStickyAtc(element);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initializeHasthrangStickyAtc());
} else {
  initializeHasthrangStickyAtc();
}

document.addEventListener('product-info:loaded', (event) => initializeHasthrangStickyAtc(event.target));
document.addEventListener('shopify:section:load', (event) => initializeHasthrangStickyAtc(event.target));
